import {getLangFromUrl, Header} from "./Header";
import {Hero} from "./Hero";
import {Footer} from "./Footer";
import {getTextByLang} from "./Game";
import {useDispatch, useSelector} from "react-redux";
import React, {useEffect, useState} from "react";
import {useParams, useLocation} from "react-router-dom";
import {AppDispatch, RootState} from "./store/store";
import {fetchGame} from "./store/gamesSlice";
import {Divider, Flex, Spin} from "antd";
import {Loading} from "./Loading";
import Markdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import {
    faBaby,
    faClock,
    faDownload,
    faUser,
    faUserFriends,
    faUserGroup,
    faUsers
} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {translations} from "./Translations";
import {Gallery} from "./Gallery";
import {StandaloneGalleryItem} from "./StandaloneGalleryItem";
import {RelationGrid} from "./RelationGrid";
import {NotFound} from "./NotFound";

export const GamePage = () => {
    const {game: gameId} = useParams<{ game: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const game = useSelector((state: RootState) => state.games.current);
    const loading = useSelector((state: RootState) => state.games.currentLoading);
    const error = useSelector((state: RootState) => state.games.currentError);
    useLocation(); // re-render on URL change so getLangFromUrl() stays in sync
    const lang = getLangFromUrl();

    useEffect(() => {
        if (gameId) dispatch(fetchGame(gameId));
    }, [gameId, dispatch]);

    if (loading) return <Loading/>;
    if (!game || error) return <NotFound/>;

    return (
            <div className={'main ' + (lang === 'he' ? "rtl" : 'ltr')} dir={lang === 'he' ? 'rtl' : 'ltr'}>
                <Header/>
                {game && <>
                    <Hero game={game} navigation={false}/>
                    <Flex className={"game-details"} vertical={true}>
                        <div className={"game-content"}>
                            <div className={"game-info"}>
                                <h1 style={{marginBottom: 10}}>{getTextByLang(game.name, lang)}</h1>
                                <div style={{
                                    color: "#aaa",
                                    marginBottom: 40
                                }}>{getTextByLang(game.description, lang)}</div>
                                <Markdown
                                    remarkPlugins={[remarkBreaks, remarkGfm]}>{getTextByLang(game.details, lang)}</Markdown>
                            </div>
                            <div className={"game-properties"}>
                                {game.players && <GameProperty label={translations[lang].playersCount} icon={faUsers}
                                                               value={getTextByLang(game.players, lang)}/>}
                                <Divider/>
                                {game.duration && <GameProperty label={translations[lang].timeMinutes} icon={faClock}
                                                                value={getTextByLang(game.duration, lang)}/>}
                                <Divider/>
                                {game.age && <GameProperty label={translations[lang].age} icon={faUserFriends}
                                                           value={`${getTextByLang(game.age, lang)}+`}/>}
                            </div>

                        </div>

                        {(game.gallery && game.gallery.length > 0) && (
                            <>
                                {game.gallery.filter(g => g.standalone)[0] &&
                                    <StandaloneGalleryItem attachment={
                                        game.gallery.filter(g => g.standalone)[0]
                                    }
                                    />
                                }
                                <div className={"gallery-wrapper"}>
                                    <div className={"gallery-section"}>
                                        <h2>{translations[lang].gallery}</h2>
                                        <div
                                            className={"gallery-description"}>{translations[lang].galleryDescription}</div>
                                        <Gallery items={game.gallery.filter((g) => !g.standalone)} height={320}
                                                 lang={lang} rtl={lang === 'he'}/>
                                    </div>
                                </div>
                            </>
                        )}
                        {(game.downloads && game.downloads.length > 0) && (
                            <div className="downloads-wrapper">
                                <div className="downloads-section">
                                    <h2 className="downloads-title">{(translations as any)[lang].downloads}</h2>
                                    <p className="downloads-desc">{(translations as any)[lang].downloadsDescription}</p>
                                    <ul className="downloads-list">
                                        {game.downloads.map((item, i) => {
                                            const url = getTextByLang(item.file, lang);
                                            const name = getTextByLang(item.name, lang);
                                            const desc = getTextByLang(item.description, lang);
                                            return (
                                                <li key={i} className="download-item">
                                                    <div className="download-item__info">
                                                        {name && <div className="download-item__name">{name}</div>}
                                                        {desc && <div className="download-item__desc">{desc}</div>}
                                                    </div>
                                                    <DownloadButton
                                                        url={url}
                                                        filename={url.split("/").pop() || "download"}
                                                        label={(translations as any)[lang].download}
                                                    />
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        )}
                        {game.gallery && game.gallery.filter(g => g.standalone)[1] &&
                            <StandaloneGalleryItem attachment={
                                game.gallery.filter(g => g.standalone)[1]
                            }
                            />
                        }
                        {(game.series && game.series.length > 0) && (
                            <RelationGrid
                                items={game.series}
                                lang={lang}
                                title={(translations as any)[lang].series}
                                subtitle={(translations as any)[lang].seriesDescription.replace("{series}", getTextByLang(game.name, lang))}
                            />
                        )}
                        {(game.addons && game.addons.length > 0) && (
                            <RelationGrid
                                inverse={true}
                                items={game.addons}
                                lang={lang}
                                title={(translations as any)[lang].addons}
                                subtitle={(translations as any)[lang].addonsDescription}
                            />
                        )}
                    </Flex>
                </>}
                <Footer/>
            </div>
    );
}

const DownloadButton = ({ url, filename, label }: { url: string; filename: string; label: string }) => {
    const [loading, setLoading] = useState(false);

    const handleClick = async () => {
        if (!url || loading) return;
        setLoading(true);
        try {
            const res = await fetch(url);
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(blobUrl);
        } finally {
            setLoading(false);
        }
    };

    return (
        <button style={{border:'none'}} onClick={handleClick} disabled={loading} className="download-item__btn">
            <FontAwesomeIcon icon={faDownload}/>
            {label}
        </button>
    );
};

export const GameProperty = (
    {label, icon, value}: { label: string, icon: any, value: string }
) => {

    return (
        <Flex
            vertical={true}
            justify={'center'}
            align={'center'}
            gap={20}
        >
            <div className={"label"}>{label}</div>
            <FontAwesomeIcon icon={icon} className={"icon-gradient"} style={{fontSize: 48}}/>
            <div className={"value"}>{value}</div>
        </Flex>
    )
}
